import { DateTime } from "luxon";

import type { DateRange, Resolution } from "../shared/types";
import type { SignatureDataPoint } from "./BaseSignatureDiagramChart";

/** Raw point with datetime and value, used for filtering and aggregation. */
export type RawDataPoint = { datetime: string; value: number };

/**
 * Filter an array of points to the given date range (inclusive).
 * Single place for date-range filtering in signature diagram flows.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function filterToDateRange<T extends RawDataPoint>(points: T[], dateRange: DateRange): T[] {
  const start = dateRange?.startDate;
  const end = dateRange?.endDate;
  if (!points?.length || !start || !end) return points;
  return points.filter((point) => {
    const pointDate = DateTime.fromISO(point.datetime);
    return pointDate >= start && pointDate <= end;
  });
}

/**
 * Build a map from time key (hour ISO or date string) to outdoor temperature.
 * Used so signature series can look up outdoor temp by the same key used for aggregation.
 */
export function buildWeatherMap(data: RawDataPoint[], resolution: Resolution): Map<string, number> {
  const map = new Map<string, number>();
  if (!data?.length) return map;

  data.forEach((point) => {
    const date = DateTime.fromISO(point.datetime);
    const key = resolution === "daily" ? date.toFormat("yyyy-MM-dd") : date.startOf("hour").toISO();
    map.set(key, point.value);
  });
  return map;
}

/**
 * Aggregate raw points into time-unit buckets and pair with outdoor temp from the map.
 * Used for hourly/daily resolution (not as_measured).
 */
export function aggregateDataByTime(
  meterData: RawDataPoint[],
  weatherDataMap: Map<string, number>,
  timeUnit: "hour" | "day"
): SignatureDataPoint[] {
  if (!meterData?.length) return [];

  const aggregated: Record<
    string,
    { values: number[]; timestamp: number; outdoorTemp: number | null }
  > = {};

  meterData.forEach((point) => {
    const date = DateTime.fromISO(point.datetime);
    const timeKey = timeUnit === "day" ? date.toFormat("yyyy-MM-dd") : date.startOf("hour").toISO();

    if (!aggregated[timeKey]) {
      const timestampBase =
        timeUnit === "day" ? date.startOf("day").toMillis() : date.startOf("hour").toMillis();
      aggregated[timeKey] = {
        values: [],
        timestamp: timestampBase,
        outdoorTemp: weatherDataMap.get(timeKey) ?? null,
      };
    }
    aggregated[timeKey].values.push(point.value);
  });

  return Object.values(aggregated)
    .filter(
      (bucket): bucket is typeof bucket & { outdoorTemp: number } => bucket.outdoorTemp != null
    )
    .map((bucket) => ({
      value: bucket.values.reduce((sum, val) => sum + val, 0) / bucket.values.length,
      outdoor: bucket.outdoorTemp,
      timestamp: bucket.timestamp,
    }));
}

/**
 * Map raw data points 1:1 with same-hour outdoor temp from the map.
 * Used for as_measured resolution.
 */
export function mapDataWithOutdoorTemp(
  meterData: RawDataPoint[],
  weatherDataMap: Map<string, number>
): SignatureDataPoint[] {
  if (!meterData?.length) return [];

  return meterData
    .map((point) => {
      const pointDate = DateTime.fromISO(point.datetime);
      const hourKey = pointDate.startOf("hour").toISO();
      const outdoor = weatherDataMap.get(hourKey);
      return outdoor === undefined || outdoor === null
        ? null
        : { value: point.value, outdoor, timestamp: pointDate.toMillis() };
    })
    .filter((p): p is SignatureDataPoint => p !== null);
}

/**
 * Transform raw meter data to signature points based on resolution.
 * Single entry point for "value vs outdoor" transform used by energy signature.
 */
export function toSignatureDataPoints(
  meterData: RawDataPoint[],
  weatherDataMap: Map<string, number>,
  resolution: Resolution
): SignatureDataPoint[] {
  if (resolution === "as_measured") {
    return mapDataWithOutdoorTemp(meterData, weatherDataMap);
  }
  return aggregateDataByTime(meterData, weatherDataMap, resolution === "daily" ? "day" : "hour");
}
